import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Icon } from "@iconify/react/dist/iconify.js";
import NavHeader from "./NavHeader";
import LoadingSpinner from './LoadingSpinner';

import { useAuth } from "../core/AuthContext";
import "../styles/UserProfile.css";

import { URL } from "../core/config";
import { getDepartmentIcon, getDepartmentName } from "../core/constants/departments";
import { useDepartments } from "../core/DepartmentsContext";
import { useDispatch } from "react-redux";
import {
  canViewUserSensitiveData,
  canViewUserContactDetails,
} from "../core/utils/permissionUtils";
import { logoutUser } from "../core/store/actions/auth";
import { getRoleDisplayName } from "../core/utils/roleUtils";
import RolePositionEmailContacts from "./profile/RolePositionEmailContacts";

const usersURL = URL + "/users";
const eventsURL = URL + "/events";
const DATE_FORMAT = "dd/MM/yyyy";

function UserProfileDetail() {
  const authContext = useAuth();
  const contextUser = authContext.user;
  const dispatch = useDispatch();
  const { departments: catalog } = useDepartments();

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserAndEvents = async () => {
      try {
        const userResponse = await axios.get(usersURL + `/${userId}`);
        setUser(userResponse.data);
        const eventsResponse = await axios.get(
          `${eventsURL}/eventsbyuser/${userId}`,
        );
        setEvents(eventsResponse.data);
      } catch (err) {
        setError("Error fetching user or events data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndEvents();
  }, [userId]);

  if (loading) return <div className="page-container"><LoadingSpinner message="Loading profile" /></div>;
  if (error) return <p>{error}</p>;

  const formattedDate = user.birthDate
    ? format(new Date(user.birthDate), DATE_FORMAT)
    : "";
  function formatDateCustom(dataString) {
    return dataString ? format(new Date(dataString), "dd MMM") : "";
  }

  return (
    <div className="page-container">
      <NavHeader />

      <h1 className="page-title">{user.name}</h1>
      <div className="user-list-container">
        <div className="user-list-lateral-section" style={{ padding: "10px" }}>
          <div
            className={"row section"}
            style={{ margin: "0", marginBottom: "30px" }}
          >
            <div className={"main-descriptions user-title"}>User Details</div>

            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:id" className="detail-icon" />
                Full Name
              </span>
              <p style={{ paddingLeft: "23px" }}>{user.fullName}</p>
            </div>
            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:admin-users" className="detail-icon" />
                Nickname
              </span>
              <p style={{ paddingLeft: "23px" }}>{user.nickname}</p>
            </div>
            <div className="detail-info">
              <span className="detail-title">
                <Icon
                  icon="dashicons:buddicons-community"
                  className="detail-icon"
                />
                Birthday
              </span>
              <p style={{ paddingLeft: "23px" }}>{formattedDate}</p>
            </div>
            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:admin-site" className="detail-icon" />
                Nationality
              </span>
              <p style={{ paddingLeft: "23px" }}>{user.nationality}</p>
            </div>
            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:flag" className="detail-icon" />
                Naturality
              </span>
              <p style={{ paddingLeft: "23px" }}>{user.naturality}</p>
            </div>
            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:businessman" className="detail-icon" />
                Professional Status
              </span>
              <p style={{ paddingLeft: "23px" }}>{user.professionalStatus}</p>
            </div>

            {canViewUserContactDetails(contextUser, userId) && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="dashicons:admin-home" className="detail-icon" />
                  Address
                </span>
                <p style={{ paddingLeft: "23px" }}>{user.address}</p>
              </div>
            )}

            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:airplane" className="detail-icon" />
                Mobility
              </span>
              <p style={{ paddingLeft: "23px" }}>
                {user.mobilityPlace}{" "}
                {user.mobilityProgram ? user.mobilityProgram : "(No program)"}
              </p>
            </div>
          </div>
        </div>

        <div className="user-list-main-section" style={{ padding: "10px" }}>
          <div
            className={"row section"}
            style={{ margin: "0", marginBottom: "30px" }}
          >
            <div className={"main-descriptions user-title"}>ESN Details</div>

            <div className="detail-info">
              <span className="detail-title" style={{ marginRight: "10px" }}>
                <Icon
                  icon="dashicons:universal-access-alt"
                  className="detail-icon"
                />
                Departments
              </span>
              <span
                className="user-item-departments"
                style={{ paddingLeft: "23px", marginBottom: "16px" }}
              >
                {user.departments.map((department, index) => (
                  <span key={index} className="user-item-department">
                    <div className="dep-icon">
                      {getDepartmentIcon(department, {}, catalog)}
                    </div>
                    <div className="dep-name">{getDepartmentName(department)}</div>
                  </span>
                ))}
              </span>
            </div>

            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:star-filled" className="detail-icon" />
                Membership
              </span>{" "}
              <p style={{ paddingLeft: "23px", fontStyle: "italic" }}>
                {user.membershipType}
              </p>
            </div>

            {user.roles && user.roles.length > 0 && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="dashicons:groups" className="detail-icon" />
                  Role(s)
                </span>
                <p style={{ paddingLeft: "23px" }}>
                  {user.roles.map((role) => getRoleDisplayName(role)).filter(Boolean).join(", ")}
                </p>
              </div>
            )}

            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:calendar" className="detail-icon" />
                Date Joined
              </span>{" "}
              <p style={{ paddingLeft: "23px" }}>
                {formatDateCustom(user.dateJoined)}
              </p>
            </div>

            {canViewUserSensitiveData(contextUser, userId) && (
            <div className="detail-info">
              <span className="detail-title">
                <Icon icon="dashicons:shield" className="detail-icon" />
                Platform login
              </span>{" "}
              <p style={{ paddingLeft: "23px" }}>
                {user.membershipType === "Alumni" || user.membershipType === "Unbound"
                  ? "Inactive (Alumni / Unbound)"
                  : user.loginActive === true
                    ? "Active"
                    : "Inactive (pending HR)"}
              </p>
            </div>
            )}
          </div>

          <div
            className={"row section"}
            style={{ margin: "0", marginBottom: "30px" }}
          >
            <div className={"main-descriptions user-title"}>Contacts</div>

            {user.institutionalEmail && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="dashicons:email" className="detail-icon" /> ESN
                  email
                </span>
                <p style={{ paddingLeft: "23px" }}>{user.institutionalEmail}</p>
              </div>
            )}

            <RolePositionEmailContacts roles={user.roles} />

            {/* Show personal email for high-level users, self-view, or when no institutional email */}
            {(canViewUserContactDetails(contextUser, userId) || !user.institutionalEmail) && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="dashicons:email" className="detail-icon" /> Personal
                  email
                </span>
                <p style={{ paddingLeft: "23px" }}>{user.personalEmail}</p>
              </div>
            )}

            {canViewUserContactDetails(contextUser, userId) && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="ic:baseline-phone" className="detail-icon" /> Phone
                  Number
                </span>
                <p style={{ paddingLeft: "23px" }}>{user.phone}</p>
              </div>
            )}

            {canViewUserSensitiveData(contextUser, userId) && (
              <div className="detail-info">
                <span className="detail-title">
                  <Icon icon="ic:baseline-sos" className="detail-icon" />
                  Emergency Contact
                </span>
                <p style={{ paddingLeft: "23px" }}>
                  {user.emergencyName
                    ? `${user.emergencyName} ${user.emergencyRelationship}: ${user.emergencyPhone}`
                    : "-"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* <div className="user-list-right-section" style={{ padding: "10px" }}>
          <div
            className={"row section"}
            style={{ margin: "0", marginBottom: "30px" }}
          >
            <div className={"main-descriptions user-title"}>Achievements</div>
            {user.achievements && user.achievements.length > 0 ? (
              user.achievements.map((achievement, index) => (
                <div key={index} className={"achievements-container"}>
                  <img src={StripsHead} className={"achievement-image"} />
                  <div className={"achievement-date"}>
                    {formatDateCustom(achievement.date)}
                  </div>
                  <p>{achievement.achievement}</p>
                </div>
              ))
            ) : (
              <div className={"achievements-container"}>
                <img src={StripsHead} className={"achievement-image"} />
                <div className={"achievement-date"}>2023-2024</div>
              </div>
            )}
          </div>
        </div>*/}
      </div>

      <footer>
        {userId === contextUser._id && (
          <button
            className="button button-transparent-black"
            onClick={handleLogout}
          >
            <Icon
              icon="fluent:arrow-exit-20-filled"
              width="1.5em"
              height="1.5em"
              style={{ marginRight: "10px" }}
            />
            Log Out
          </button>
        )}
      </footer>
    </div>
  );
}

export default UserProfileDetail;
